import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { alpha, styled, useTheme as useMuiTheme } from '@mui/material/styles';
import { Calendar, CalendarClock, CalendarPlus, ChevronLeft, ChevronRight, Globe, Lock, Plus, Square, Trash2, X } from 'lucide-react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import itLocale from '@fullcalendar/core/locales/it';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const COLORI_PRESET = [
  '#1a73e8',
  '#33b679',
  '#f4511e',
  '#8e24aa',
  '#ef6c00',
  '#f6bf26',
  '#0b8043',
  '#3f51b5',
  '#5f6368'
];

const formatTitle = (title) => {
  if (!title) return '';
  return title.replace(/\b(\d)\b/g, '0$1');
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const addMonths = (date, delta) => new Date(date.getFullYear(), date.getMonth() + delta, 1);

const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

const getMonthLabel = (date) => {
  const label = date.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const buildMonthGrid = (monthDate) => {
  const first = startOfMonth(monthDate);
  const offset = first.getDay();
  const days = getDaysInMonth(first);
  const totalCells = Math.ceil((offset + days) / 7) * 7;
  const cells = Array.from({ length: totalCells }, (_, idx) => {
    const dayNumber = idx - offset + 1;
    if (dayNumber < 1 || dayNumber > days) return null;
    return new Date(first.getFullYear(), first.getMonth(), dayNumber);
  });
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
};

const pad2 = (n) => String(n).padStart(2, '0');

const toDateTimeLocalValue = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const toDateOnlyValue = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const fromDateTimeLocalValue = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fromDateOnlyValue = (value) => {
  if (!value) return null;
  const d = new Date(`${value}T00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const addMinutes = (date, minutes) => {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() + minutes);
  return d;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const atStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const startOfWeekMonday = (date) => {
  const d = atStartOfDay(new Date(date));
  const day = d.getDay(); // 0=Dom ... 6=Sab
  const diff = (day + 6) % 7; // Lun=0 ... Dom=6
  d.setDate(d.getDate() - diff);
  return d;
};

const dateKey = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

export default function Agenda({ embedded = false }) {
  const calendarRef = useRef(null);
  const { user } = useAuth();
  const roleName = String(user?.role_name || user?.role || '').toLowerCase();
  const isAdmin = roleName === 'admin';
  const isAgency = roleName === 'agenzia';
  const muiTheme = useMuiTheme();

  const [currentView, setCurrentView] = useState('timeGridWeek');
  const [calendarTitle, setCalendarTitle] = useState('');
  const [calendari, setCalendari] = useState([]);
  const [calendariLoading, setCalendariLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createNome, setCreateNome] = useState('');
  const [createDescrizione, setCreateDescrizione] = useState('');
  const [createColore, setCreateColore] = useState('#1a73e8');
  const [createVisibilita, setCreateVisibilita] = useState('privata');
  const [createTipo, setCreateTipo] = useState('personale');
  const [createIcona, setCreateIcona] = useState('calendario');
  const [createSaving, setCreateSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteEventCount, setDeleteEventCount] = useState(null);
  const [deleteEventCountLoading, setDeleteEventCountLoading] = useState(false);
  const [rawEvents, setRawEvents] = useState([]);
  const [miniRawEvents, setMiniRawEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  const [eventSaving, setEventSaving] = useState(false);
  const [eventMode, setEventMode] = useState('create');
  const [eventId, setEventId] = useState(null);
  const [eventTitolo, setEventTitolo] = useState('');
  const [eventLuogo, setEventLuogo] = useState('');
  const [eventNote, setEventNote] = useState('');
  const [eventAllDay, setEventAllDay] = useState(false);
  const [eventCalendarId, setEventCalendarId] = useState('');
  const [eventStart, setEventStart] = useState('');
  const [eventEnd, setEventEnd] = useState('');
  const [eventStartDate, setEventStartDate] = useState('');
  const [eventEndDate, setEventEndDate] = useState('');

  const today = useMemo(() => new Date(), []);
  const [selectedDate, setSelectedDate] = useState(today);
  const [miniMonth, setMiniMonth] = useState(startOfMonth(today));
  const lastEventsRangeRef = useRef({ start: null, end: null });
  const lastMiniEventsRangeRef = useRef({ start: null, end: null });
  const selectedWeekStart = useMemo(() => startOfWeekMonday(selectedDate), [selectedDate]);
  const selectedWeekEnd = useMemo(() => addDays(selectedWeekStart, 7), [selectedWeekStart]);
  const [hoverAnchorEl, setHoverAnchorEl] = useState(null);
  const [hoverEventData, setHoverEventData] = useState(null);

  const resetCreateForm = () => {
    setCreateNome('');
    setCreateDescrizione('');
    setCreateColore('#1a73e8');
    setCreateVisibilita('privata');
    setCreateTipo('personale');
    setCreateIcona('calendario');
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setCalendariLoading(true);
      try {
        const res = await api.get('/calendari/sidebar');
        const list = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
        const normalized = list
          .map((row) => ({
            id: Number(row.id),
            nome: String(row.nome || '').trim(),
            descrizione: row.descrizione != null ? String(row.descrizione) : null,
            colore: String(row.colore || '#1a73e8'),
            icona: row.icona != null ? String(row.icona) : null,
            tipo: row.tipo != null ? String(row.tipo) : null,
            visibilita: row.visibilita != null ? String(row.visibilita) : null,
            can_delete: Number(row.can_delete) ? 1 : 0,
            id_proprietario: row.id_proprietario != null ? Number(row.id_proprietario) : null,
            proprietario_nome: row.proprietario_nome != null ? String(row.proprietario_nome) : null,
            proprietario_email: row.proprietario_email != null ? String(row.proprietario_email) : null,
            visibile: Number(row.visibile) ? 1 : 0,
            ordine: Number.isFinite(Number(row.ordine)) ? Number(row.ordine) : 0
          }))
          .filter((c) => Number.isFinite(c.id) && c.id > 0 && c.nome);

        if (mounted) {
          if (isAgency) {
            const userId = Number(user?.id);
            const onlyPrivateMine = normalized.filter((c) => {
              const isPrivato = String(c.visibilita || '').toLowerCase() === 'privata';
              return isPrivato && Number(c.id_proprietario) === userId;
            });
            onlyPrivateMine.sort((a, b) => a.ordine - b.ordine || a.nome.localeCompare(b.nome, 'it'));
            setCalendari(onlyPrivateMine);
          } else if (normalized.length > 0) {
            normalized.sort((a, b) => a.ordine - b.ordine || a.nome.localeCompare(b.nome, 'it'));
            setCalendari(normalized);
          } else {
            setCalendari([]);
          }
        }
      } catch (error) {
        if (mounted) setCalendari([]);
      } finally {
        if (mounted) {
          setCalendariLoading(false);
        }
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [isAgency, user?.id]);

  const calendariByNome = useMemo(() => {
    const map = new Map();
    calendari.forEach((c) => map.set(c.nome, c));
    return map;
  }, [calendari]);

  const calendariById = useMemo(() => {
    const map = new Map();
    calendari.forEach((c) => map.set(Number(c.id), c));
    return map;
  }, [calendari]);

  const events = useMemo(() => {
    return rawEvents
      .filter((e) => {
        const nome = e.extendedProps?.calendarNome;
        const id = e.extendedProps?.calendarId;
        const cal =
          (Number.isFinite(Number(id)) ? calendariById.get(Number(id)) : null) ||
          (nome ? calendariByNome.get(nome) : null);
        if (!cal) return !isAgency;
        return Number(cal.visibile) !== 0;
      })
      .map((e) => {
        const nome = e.extendedProps?.calendarNome;
        const id = e.extendedProps?.calendarId;
        const cal =
          (Number.isFinite(Number(id)) ? calendariById.get(Number(id)) : null) ||
          (nome ? calendariByNome.get(nome) : null);
        const color = cal?.colore || '#2563eb';
        const bg = muiTheme.palette.mode === 'dark' ? muiTheme.palette.background.paper : '#ffffff';
        return {
          ...e,
          backgroundColor: bg,
          borderColor: color,
          textColor: color
        };
      });
  }, [calendariById, calendariByNome, isAgency, rawEvents, muiTheme.palette.background.paper, muiTheme.palette.mode]);

  const miniEventDays = useMemo(() => {
    const keys = new Set();
    miniRawEvents.forEach((e) => {
      const nome = e.extendedProps?.calendarNome;
      const id = e.extendedProps?.calendarId;
      const cal =
        (Number.isFinite(Number(id)) ? calendariById.get(Number(id)) : null) ||
        (nome ? calendariByNome.get(nome) : null);
      if (!cal && isAgency) return;
      if (cal && Number(cal.visibile) === 0) return;

      const start = e.start ? new Date(e.start) : null;
      const end = e.end ? new Date(e.end) : null;
      if (!start || Number.isNaN(start.getTime())) return;

      const allDay = Boolean(e.allDay);
      if (!end || Number.isNaN(end.getTime())) {
        keys.add(dateKey(start));
        return;
      }

      if (allDay) {
        const cursor = new Date(start);
        while (cursor < end) {
          keys.add(dateKey(cursor));
          cursor.setDate(cursor.getDate() + 1);
        }
        return;
      }

      keys.add(dateKey(start));
      const cursor = new Date(start);
      while (cursor < end) {
        keys.add(dateKey(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    return keys;
  }, [calendariById, calendariByNome, isAgency, miniRawEvents]);

  const goPrev = () => calendarRef.current?.getApi()?.prev();
  const goNext = () => calendarRef.current?.getApi()?.next();
  const goToday = () => calendarRef.current?.getApi()?.today();

  const loadEvents = async ({ start, end }) => {
    if (!start || !end) return;
    const startIso = start.toISOString();
    const endIso = end.toISOString();
    const last = lastEventsRangeRef.current;
    if (last.start === startIso && last.end === endIso) return;

    lastEventsRangeRef.current = { start: startIso, end: endIso };
    setEventsLoading(true);
    try {
      const res = await api.get('/eventi', { params: { start: startIso, end: endIso } });
      const list = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
      const normalized = list
        .map((row) => ({
          id: String(row.id),
          title: String(row.title || row.titolo || '').trim(),
          start: row.start,
          end: row.end,
          allDay: Boolean(row.allDay),
          extendedProps: {
            calendarId: row.extendedProps?.calendarId ?? row.id_calendario ?? row.calendarId ?? null,
            calendarNome: row.extendedProps?.calendarNome ?? row.calendario_nome ?? row.calendarNome ?? null,
            luogo: row.extendedProps?.luogo ?? row.luogo ?? null,
            note: row.extendedProps?.note ?? row.note ?? null,
            timezone: row.extendedProps?.timezone ?? row.timezone ?? 'Europe/Rome'
          }
        }))
        .filter((e) => e.id && e.title && e.start && e.end);
      setRawEvents(normalized);
    } catch (error) {
      toast.error('Errore caricamento eventi');
      setRawEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  const loadMiniEventsForMonth = async (monthDate) => {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = addMonths(monthStart, 1);
    const startIso = monthStart.toISOString();
    const endIso = monthEnd.toISOString();
    const last = lastMiniEventsRangeRef.current;
    if (last.start === startIso && last.end === endIso) return;

    lastMiniEventsRangeRef.current = { start: startIso, end: endIso };
    try {
      const res = await api.get('/eventi', { params: { start: startIso, end: endIso } });
      const list = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
      const normalized = list
        .map((row) => ({
          id: String(row.id),
          title: String(row.title || row.titolo || '').trim(),
          start: row.start,
          end: row.end,
          allDay: Boolean(row.allDay),
          extendedProps: {
            calendarId: row.extendedProps?.calendarId ?? row.id_calendario ?? row.calendarId ?? null,
            calendarNome: row.extendedProps?.calendarNome ?? row.calendario_nome ?? row.calendarNome ?? null,
            luogo: row.extendedProps?.luogo ?? row.luogo ?? null,
            note: row.extendedProps?.note ?? row.note ?? null,
            timezone: row.extendedProps?.timezone ?? row.timezone ?? 'Europe/Rome'
          }
        }))
        .filter((e) => e.id && e.title && e.start && e.end);
      setMiniRawEvents(normalized);
    } catch (_error) {
    }
  };

  useEffect(() => {
    loadMiniEventsForMonth(miniMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miniMonth]);

  const refreshEventsForCurrentView = () => {
    const apiRef = calendarRef.current?.getApi();
    const view = apiRef?.view;
    const start = view?.activeStart ? new Date(view.activeStart) : null;
    const end = view?.activeEnd ? new Date(view.activeEnd) : null;
    if (!start || !end) return;
    lastEventsRangeRef.current = { start: null, end: null };
    loadEvents({ start, end });
  };

  const refreshMiniEventsForCurrentMonth = () => {
    lastMiniEventsRangeRef.current = { start: null, end: null };
    loadMiniEventsForMonth(miniMonth);
  };

  const handleDatesSet = (arg) => {
    setCalendarTitle(formatTitle(arg.view?.title));
    const apiRef = calendarRef.current?.getApi();
    const focused = apiRef?.getDate ? new Date(apiRef.getDate()) : new Date(arg.start);
    setSelectedDate(focused);
    setMiniMonth(startOfMonth(focused));
    if (arg?.start && arg?.end) {
      loadEvents({ start: new Date(arg.start), end: new Date(arg.end) });
    }
  };

  const toggleCalendar = async (id) => {
    const current = calendari.find((c) => c.id === id);
    if (!current) return;
    const nextVisibile = current.visibile ? 0 : 1;

    setCalendari((prev) => prev.map((c) => (c.id === id ? { ...c, visibile: nextVisibile } : c)));

    if (!Number.isFinite(id) || id <= 0) {
      return;
    }

    try {
      await api.put(`/calendari/${id}/preferenze`, { visibile: nextVisibile, ordine: current.ordine });
    } catch (error) {
      setCalendari((prev) => prev.map((c) => (c.id === id ? { ...c, visibile: current.visibile } : c)));
      toast.error('Errore nel salvataggio delle preferenze calendario');
    }
  };

  const handleCreateCalendar = async () => {
    const nome = String(createNome || '').trim();
    if (!nome) {
      toast.error('Inserisci il nome del calendario');
      return;
    }

    setCreateSaving(true);
    try {
      const finalTipo = isAdmin ? createTipo : 'personale';
      const finalVisibilita = isAdmin ? createVisibilita : 'privata';
      const payload = {
        nome,
        descrizione: String(createDescrizione || '').trim() || null,
        colore: createColore,
        icona: createIcona,
        tipo: finalTipo,
        visibilita: finalVisibilita
      };
      const res = await api.post('/calendari', payload);
      const created = res.data?.data;
      if (created && created.id) {
        setCalendari((prev) => {
          const next = [...prev, created];
          next.sort(
            (a, b) =>
              (Number(a.ordine) || 0) - (Number(b.ordine) || 0) || String(a.nome).localeCompare(String(b.nome), 'it')
          );
          return next;
        });
      }
      setCreateOpen(false);
      resetCreateForm();
    } catch (error) {
      toast.error('Errore nella creazione del calendario');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleDeleteCalendar = async () => {
    const target = deleteTarget;
    const id = Number(target?.id);
    if (!Number.isFinite(id) || id <= 0) {
      setDeleteOpen(false);
      setDeleteTarget(null);
      setDeleteEventCount(null);
      return;
    }
    setDeleteSaving(true);
    try {
      await api.delete(`/calendari/${id}`);
      setCalendari((prev) => prev.filter((c) => c.id !== id));
      setRawEvents((prev) => prev.filter((e) => String(e.extendedProps?.calendarId) !== String(id)));
      setDeleteOpen(false);
      setDeleteTarget(null);
      setDeleteEventCount(null);
    } catch (error) {
      toast.error('Errore eliminazione calendario');
    } finally {
      setDeleteSaving(false);
    }
  };

  const openDeleteCalendarDialog = async (calendar) => {
    setDeleteTarget(calendar);
    setDeleteEventCount(null);
    setDeleteEventCountLoading(true);
    setDeleteOpen(true);
    try {
      const res = await api.get(`/calendari/${calendar.id}/eventi-count`);
      const count = Number(res.data?.data?.count);
      setDeleteEventCount(Number.isFinite(count) ? count : 0);
    } catch (_error) {
      setDeleteEventCount(null);
    } finally {
      setDeleteEventCountLoading(false);
    }
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    calendarRef.current?.getApi()?.changeView(view);
  };

  const formatEventTimeRange = (eventObj) => {
    if (!eventObj) return '';
    if (eventObj.allDay) return 'Tutto il giorno';
    const s = eventObj.start instanceof Date ? eventObj.start : eventObj.start ? new Date(eventObj.start) : null;
    const e = eventObj.end instanceof Date ? eventObj.end : eventObj.end ? new Date(eventObj.end) : null;
    const fmt = (d) => d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    if (s && e && !Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime())) return `${fmt(s)} - ${fmt(e)}`;
    if (s && !Number.isNaN(s.getTime())) return fmt(s);
    return '';
  };

  const resetEventForm = () => {
    setEventId(null);
    setEventTitolo('');
    setEventLuogo('');
    setEventNote('');
    setEventAllDay(false);
    setEventCalendarId('');
    setEventStart('');
    setEventEnd('');
    setEventStartDate('');
    setEventEndDate('');
  };

  const openCreateEvent = ({ start, end, allDay }) => {
    if (isAgency && calendari.length === 0) {
      toast.error('Nessun calendario disponibile');
      return;
    }
    const visibleCalendars = calendari.filter((c) => Number(c.visibile) !== 0);
    const defaultCalendar = visibleCalendars[0] || calendari[0] || null;

    setEventMode('create');
    setEventSaving(false);
    setEventId(null);
    setEventTitolo('');
    setEventLuogo('');
    setEventNote('');
    setEventAllDay(Boolean(allDay));
    setEventCalendarId(defaultCalendar ? String(defaultCalendar.id) : '');

    if (allDay) {
      const startDate = start ? toDateOnlyValue(start) : toDateOnlyValue(new Date());
      const endDate = end ? toDateOnlyValue(addDays(end, -1)) : startDate;
      setEventStartDate(startDate);
      setEventEndDate(endDate || startDate);
      setEventStart('');
      setEventEnd('');
    } else {
      const s = start ? new Date(start) : new Date();
      const e = end ? new Date(end) : addMinutes(s, 60);
      setEventStart(toDateTimeLocalValue(s));
      setEventEnd(toDateTimeLocalValue(e));
      setEventStartDate('');
      setEventEndDate('');
    }

    setEventOpen(true);
  };

  const openEditEvent = (fcEvent) => {
    const props = fcEvent.extendedProps || {};
    const calendarNome = props.calendarNome ? String(props.calendarNome) : '';
    const calendarIdFromProps = props.calendarId != null ? String(props.calendarId) : '';
    const fromNome = calendarNome ? calendariByNome.get(calendarNome) : null;
    const calendarId = calendarIdFromProps || (fromNome ? String(fromNome.id) : '');

    setEventMode('edit');
    setEventSaving(false);
    setEventId(String(fcEvent.id));
    setEventTitolo(String(fcEvent.title || ''));
    setEventLuogo(props.luogo != null ? String(props.luogo) : '');
    setEventNote(props.note != null ? String(props.note) : '');
    setEventAllDay(Boolean(fcEvent.allDay));
    setEventCalendarId(calendarId);

    if (fcEvent.allDay) {
      const startDate = fcEvent.start ? toDateOnlyValue(fcEvent.start) : '';
      const endDate = fcEvent.end ? toDateOnlyValue(addDays(fcEvent.end, -1)) : startDate;
      setEventStartDate(startDate);
      setEventEndDate(endDate || startDate);
      setEventStart('');
      setEventEnd('');
    } else {
      setEventStart(fcEvent.start ? toDateTimeLocalValue(fcEvent.start) : '');
      setEventEnd(fcEvent.end ? toDateTimeLocalValue(fcEvent.end) : '');
      setEventStartDate('');
      setEventEndDate('');
    }

    setEventOpen(true);
  };

  const saveEvent = async () => {
    const titolo = String(eventTitolo || '').trim();
    if (!titolo) {
      toast.error('Inserisci il titolo evento');
      return;
    }

    const calendarId = String(eventCalendarId || '').trim();
    if (!calendarId) {
      toast.error('Seleziona un calendario');
      return;
    }

    const cal = calendari.find((c) => String(c.id) === calendarId) || null;
    const calendarNome = cal?.nome || null;
    const color = cal?.colore || '#2563eb';

    let startDate = null;
    let endDate = null;
    let allDay = Boolean(eventAllDay);

    if (allDay) {
      const s = fromDateOnlyValue(eventStartDate);
      const e = fromDateOnlyValue(eventEndDate);
      if (!s || !e) {
        toast.error('Seleziona data inizio e fine');
        return;
      }
      const inclusiveEnd = e;
      const exclusiveEnd = addDays(inclusiveEnd, 1);
      if (exclusiveEnd < s) {
        toast.error('La data fine deve essere uguale o successiva alla data inizio');
        return;
      }
      startDate = s;
      endDate = exclusiveEnd;
    } else {
      const s = fromDateTimeLocalValue(eventStart);
      const e = fromDateTimeLocalValue(eventEnd);
      if (!s || !e) {
        toast.error('Inserisci data/ora inizio e fine');
        return;
      }
      if (e <= s) {
        toast.error('La fine deve essere successiva all’inizio');
        return;
      }
      startDate = s;
      endDate = e;
    }

    setEventSaving(true);
    try {
      const payload = {
        id_calendario: Number(calendarId),
        titolo,
        luogo: String(eventLuogo || '').trim() || null,
        note: String(eventNote || '').trim() || null,
        tutto_il_giorno: allDay ? 1 : 0,
        timezone: 'Europe/Rome',
        data_inizio: startDate.toISOString(),
        data_fine: endDate.toISOString()
      };

      const res =
        eventMode === 'edit' && eventId
          ? await api.put(`/eventi/${eventId}`, payload)
          : await api.post('/eventi', payload);

      const saved = res.data?.data;
      if (!saved?.id) {
        throw new Error('Bad response');
      }

      setRawEvents((prev) => {
        const id = String(saved.id);
        const next = {
          id,
          title: saved.title,
          start: saved.start,
          end: saved.end,
          allDay: Boolean(saved.allDay),
          extendedProps: saved.extendedProps || { calendarId: Number(calendarId), calendarNome, luogo: payload.luogo, note: payload.note }
        };
        const idx = prev.findIndex((e) => String(e.id) === id);
        if (idx === -1) return [...prev, next];
        return prev.map((e) => (String(e.id) === id ? next : e));
      });

      const last = lastEventsRangeRef.current;
      if (last?.start && last?.end) {
        lastEventsRangeRef.current = { start: null, end: null };
        loadEvents({ start: new Date(last.start), end: new Date(last.end) });
      }
      refreshMiniEventsForCurrentMonth();

      setEventOpen(false);
      resetEventForm();
    } catch (error) {
      toast.error('Errore salvataggio evento');
    } finally {
      setEventSaving(false);
    }
  };

  const deleteEvent = async () => {
    if (!eventId) return;
    setEventSaving(true);
    try {
      await api.delete(`/eventi/${eventId}`);
      const id = String(eventId);
      setRawEvents((prev) => prev.filter((e) => String(e.id) !== id));
      setMiniRawEvents((prev) => prev.filter((e) => String(e.id) !== id));
      const last = lastEventsRangeRef.current;
      if (last?.start && last?.end) {
        lastEventsRangeRef.current = { start: null, end: null };
        loadEvents({ start: new Date(last.start), end: new Date(last.end) });
      }
      refreshMiniEventsForCurrentMonth();
      setEventOpen(false);
      resetEventForm();
    } catch (_error) {
      toast.error('Errore eliminazione evento');
    } finally {
      setEventSaving(false);
    }
  };

  const handleDateClick = (arg) => {
    if (!arg?.date) return;
    const start = arg.date;
    const end = addMinutes(start, 60);
    openCreateEvent({ start, end, allDay: Boolean(arg.allDay) });
  };

  const handleSelect = (arg) => {
    openCreateEvent({ start: arg.start, end: arg.end, allDay: Boolean(arg.allDay) });
    calendarRef.current?.getApi()?.unselect();
  };

  const applyEventDatesUpdate = async (info) => {
    const fcEvent = info?.event;
    const revert = info?.revert;
    if (!fcEvent?.id) return;
    const id = String(fcEvent.id);
    const start = fcEvent.start ? fcEvent.start.toISOString() : null;
    const end = fcEvent.end ? fcEvent.end.toISOString() : null;
    if (!start || !end) return;

    const prevSnapshot = rawEvents.find((e) => String(e.id) === id) || null;

    setRawEvents((prev) =>
      prev.map((e) =>
        String(e.id) === id
          ? {
              ...e,
              start,
              end,
              allDay: Boolean(fcEvent.allDay)
            }
          : e
      )
    );

    try {
      const res = await api.put(`/eventi/${id}`, {
        data_inizio: start,
        data_fine: end,
        tutto_il_giorno: fcEvent.allDay ? 1 : 0
      });
      const saved = res.data?.data;
      if (!saved?.id) throw new Error('Bad response');
      setRawEvents((prev) =>
        prev.map((e) =>
          String(e.id) === String(saved.id)
            ? {
                id: String(saved.id),
                title: saved.title,
                start: saved.start,
                end: saved.end,
                allDay: Boolean(saved.allDay),
                extendedProps: saved.extendedProps || e.extendedProps
              }
            : e
        )
      );
      refreshEventsForCurrentView();
      refreshMiniEventsForCurrentMonth();
    } catch (_error) {
      if (typeof revert === 'function') revert();
      if (prevSnapshot) {
        setRawEvents((prev) => prev.map((e) => (String(e.id) === id ? prevSnapshot : e)));
      }
      toast.error('Errore aggiornamento evento');
    }
  };

  const miniWeeks = useMemo(() => buildMonthGrid(miniMonth), [miniMonth]);
  const miniMonthLabel = useMemo(() => getMonthLabel(miniMonth), [miniMonth]);

  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: { xs: 'flex-start', md: 'stretch' },
          height: { xs: 'auto', md: embedded ? 'calc(100vh - 260px)' : 'calc(100vh - 220px)' }
        }}
      >
        <Box
          sx={{
            width: 300,
            flex: '0 0 300px',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            gap: 2
          }}
        >
          <StyledCard sx={{ flex: '0 0 auto' }}>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <CalendarClock size={18} color={muiTheme.palette.info.main} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Agenda
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  {miniMonthLabel}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => setMiniMonth((prev) => addMonths(prev, -1))}
                    aria-label="Mese precedente"
                    sx={{ border: '1px solid', borderColor: 'divider' }}
                  >
                    <ChevronLeft size={18} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setMiniMonth((prev) => addMonths(prev, 1))}
                    aria-label="Mese successivo"
                    sx={{ border: '1px solid', borderColor: 'divider' }}
                  >
                    <ChevronRight size={18} />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.75 }}>
                {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map((label) => (
                  <Typography
                    key={label}
                    variant="caption"
                    sx={{ textAlign: 'center', fontWeight: 700, color: 'text.secondary' }}
                  >
                    {label}
                  </Typography>
                ))}
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                {miniWeeks.flat().map((date, idx) => {
                  const isSelected = date ? isSameDay(date, selectedDate) : false;
                  const isToday = date ? isSameDay(date, today) : false;
                  const hasEvents = date ? miniEventDays.has(dateKey(date)) : false;
                  const isInSelectedWeek =
                    currentView === 'timeGridWeek' && date
                      ? atStartOfDay(date).getTime() >= selectedWeekStart.getTime() && atStartOfDay(date).getTime() < selectedWeekEnd.getTime()
                      : false;
                  return (
                    <Box
                      key={idx}
                      role={date ? 'button' : undefined}
                      tabIndex={date ? 0 : -1}
                      onClick={() => {
                        if (!date) return;
                        setSelectedDate(date);
                        calendarRef.current?.getApi()?.gotoDate(date);
                      }}
                      onKeyDown={(e) => {
                        if (!date) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          setSelectedDate(date);
                          calendarRef.current?.getApi()?.gotoDate(date);
                        }
                      }}
                      sx={{
                        height: 32,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: date ? 'pointer' : 'default',
                        userSelect: 'none',
                        position: 'relative',
                        bgcolor: isSelected
                          ? 'primary.main'
                          : isInSelectedWeek
                            ? alpha(muiTheme.palette.warning.main, muiTheme.palette.mode === 'dark' ? 0.22 : 0.14)
                            : 'transparent',
                        color: isSelected ? 'primary.contrastText' : 'text.primary',
                        border: isToday && !isSelected ? '1px solid' : '1px solid transparent',
                        borderColor: isToday && !isSelected ? 'primary.main' : 'transparent',
                        '&:hover': date && !isSelected ? { bgcolor: 'action.hover' } : undefined
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: isSelected ? 800 : 600 }}>
                        {date ? date.getDate() : ''}
                      </Typography>
                      {hasEvents ? (
                        <Box sx={{ position: 'absolute', bottom: 2, right: 2, display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: 'error.main'
                            }}
                          />
                        </Box>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </StyledCard>

          <StyledCard sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Calendar size={18} color={muiTheme.palette.info.main} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Calendari
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    resetCreateForm();
                    if (!isAdmin) setCreateVisibilita('privata');
                    setCreateOpen(true);
                  }}
                  startIcon={<Plus size={16} />}
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    minWidth: 0,
                    fontWeight: 700,
                    lineHeight: 1.1
                  }}
                >
                  Nuovo
                </Button>
              </Box>
              <Box
                sx={{
                  mt: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  overflowY: 'auto',
                  minHeight: 0,
                  flex: 1,
                  pr: 0.5
                }}
              >
                {calendariLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : calendari.length === 0 ? (
                  <Box sx={{ py: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nessun calendario disponibile
                    </Typography>
                  </Box>
                ) : (
                  calendari.map((c) => {
                    const enabled = Number(c.visibile) !== 0;
                    const isPrivato = String(c.visibilita || '').toLowerCase() === 'privata';
                    const canDelete = Number(c.can_delete) !== 0;
                    const ownerLabel =
                      isAdmin && isPrivato
                        ? c.proprietario_nome || c.proprietario_email || (c.id_proprietario != null ? `Utente #${c.id_proprietario}` : null)
                        : null;
                    return (
                      <Box
                        key={c.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleCalendar(c.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') toggleCalendar(c.id);
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          cursor: 'pointer',
                          userSelect: 'none',
                          opacity: enabled ? 1 : 0.45
                        }}
                      >
                        <Square size={14} color={c.colore} fill={c.colore} />
                        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                          {c.nome}
                        </Typography>
                        {ownerLabel ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={ownerLabel}
                          >
                            {ownerLabel}
                          </Typography>
                        ) : null}
                        {isPrivato ? (
                          <Tooltip title="Calendario privato">
                            <Box sx={{ display: 'flex', alignItems: 'center' }} aria-label="Calendario privato">
                              <Lock size={14} color={c.colore} />
                            </Box>
                          </Tooltip>
                        ) : null}
                        {canDelete ? (
                          <Tooltip title="Elimina calendario">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteCalendarDialog(c);
                              }}
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          </StyledCard>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, height: { xs: 'auto', md: '100%' }, minHeight: 0 }}>
          <StyledCard sx={{ height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button variant="outlined" size="small" onClick={goToday}>
                    Oggi
                  </Button>
                  <Button variant="outlined" size="small" onClick={goPrev} sx={{ minWidth: 0, px: 1.25 }}>
                    <ChevronLeft size={18} />
                  </Button>
                  <Button variant="outlined" size="small" onClick={goNext} sx={{ minWidth: 0, px: 1.25 }}>
                    <ChevronRight size={18} />
                  </Button>
                  <Typography variant="h6" sx={{ fontWeight: 800, ml: 1 }}>
                    {calendarTitle}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant={currentView === 'timeGridWeek' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => handleViewChange('timeGridWeek')}
                  >
                    Settimana
                  </Button>
                  <Button
                    variant={currentView === 'timeGridDay' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => handleViewChange('timeGridDay')}
                  >
                    Giorno
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Plus size={18} />}
                    onClick={() => {
                      const apiRef = calendarRef.current?.getApi();
                      const focused = apiRef?.getDate ? new Date(apiRef.getDate()) : new Date();
                      const start = new Date(focused);
                      start.setHours(9, 0, 0, 0);
                      openCreateEvent({ start, end: addMinutes(start, 60), allDay: false });
                    }}
                  >
                    nuovo Evento
                  </Button>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                p: 2,
                flex: 1,
                minHeight: 0,
                '& .fc': {
                  '--fc-border-color': muiTheme.palette.divider,
                  '--fc-page-bg-color': muiTheme.palette.background.paper,
                  '--fc-neutral-bg-color': muiTheme.palette.background.default,
                  '--fc-neutral-text-color': muiTheme.palette.text.secondary,
                  '--fc-today-bg-color': alpha(
                    muiTheme.palette.primary.main,
                    muiTheme.palette.mode === 'dark' ? 0.18 : 0.08
                  ),
                  '--fc-now-indicator-color': muiTheme.palette.error.main
                },
                '& .fc .fc-timegrid-slot-label, & .fc .fc-col-header-cell-cushion, & .fc .fc-timegrid-axis-cushion': {
                  color: muiTheme.palette.text.secondary
                },
                '& .fc .fc-daygrid-day-number, & .fc .fc-toolbar-title': {
                  color: muiTheme.palette.text.primary
                },
                '& .fc .fc-timegrid-slot, & .fc .fc-timegrid-axis, & .fc .fc-col-header-cell': {
                  backgroundColor: muiTheme.palette.background.paper
                }
              }}
            >
              <FullCalendar
                ref={calendarRef}
                plugins={[timeGridPlugin, interactionPlugin]}
                initialView="timeGridWeek"
                locale={itLocale}
                firstDay={1}
                headerToolbar={false}
                height="100%"
                expandRows
                nowIndicator
                slotMinTime="07:00:00"
                slotMaxTime="22:00:00"
                allDaySlot={false}
                selectable
                selectMirror
                editable
                eventStartEditable
                eventDurationEditable
                events={events}
                datesSet={handleDatesSet}
                dateClick={handleDateClick}
                select={handleSelect}
                eventClick={(info) => openEditEvent(info.event)}
                eventMouseEnter={(info) => {
                  const props = info?.event?.extendedProps || {};
                  const note = props.note != null ? String(props.note).trim() : '';
                  const luogo = props.luogo != null ? String(props.luogo).trim() : '';
                  const calendarNome = props.calendarNome != null ? String(props.calendarNome).trim() : '';
                  const hasAny = Boolean(note || luogo || calendarNome);
                  if (!hasAny) return;
                  if (info?.el) info.el.removeAttribute('title');
                  setHoverAnchorEl(info?.el || null);
                  setHoverEventData({
                    titolo: info?.event?.title ? String(info.event.title) : '',
                    orario: formatEventTimeRange(info?.event),
                    calendario: calendarNome,
                    luogo,
                    note
                  });
                }}
                eventMouseLeave={() => {
                  setHoverAnchorEl(null);
                  setHoverEventData(null);
                }}
                eventDrop={(info) => applyEventDatesUpdate(info)}
                eventResize={(info) => applyEventDatesUpdate(info)}
              />
            </Box>
          </StyledCard>
        </Box>
      </Box>

      <Popover
        open={Boolean(hoverAnchorEl && hoverEventData)}
        anchorEl={hoverAnchorEl}
        onClose={() => {
          setHoverAnchorEl(null);
          setHoverEventData(null);
        }}
        disableRestoreFocus
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{ pointerEvents: 'none' }}
        PaperProps={{
          sx: {
            pointerEvents: 'none',
            px: 1.25,
            py: 1,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            minWidth: 280
          }
        }}
      >
        <Table size="small" sx={{ '& td': { borderBottom: 0, py: 0.35, px: 0.75 } }}>
          <TableBody>
            {hoverEventData?.titolo ? (
              <TableRow>
                <TableCell sx={{ width: 92, color: 'text.secondary', fontWeight: 700 }}>Titolo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{hoverEventData.titolo}</TableCell>
              </TableRow>
            ) : null}
            {hoverEventData?.orario ? (
              <TableRow>
                <TableCell sx={{ width: 92, color: 'text.secondary', fontWeight: 700 }}>Orario</TableCell>
                <TableCell>{hoverEventData.orario}</TableCell>
              </TableRow>
            ) : null}
            {hoverEventData?.calendario ? (
              <TableRow>
                <TableCell sx={{ width: 92, color: 'text.secondary', fontWeight: 700 }}>Calendario</TableCell>
                <TableCell>{hoverEventData.calendario}</TableCell>
              </TableRow>
            ) : null}
            {hoverEventData?.luogo ? (
              <TableRow>
                <TableCell sx={{ width: 92, color: 'text.secondary', fontWeight: 700 }}>Luogo</TableCell>
                <TableCell>{hoverEventData.luogo}</TableCell>
              </TableRow>
            ) : null}
            {hoverEventData?.note ? (
              <TableRow>
                <TableCell sx={{ width: 92, color: 'text.secondary', fontWeight: 700 }}>Note</TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{hoverEventData.note}</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Popover>

      <Dialog
        open={eventOpen}
        onClose={() => {
          if (eventSaving) return;
          setEventOpen(false);
          resetEventForm();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Square
              size={14}
              color={calendari.find((c) => String(c.id) === String(eventCalendarId))?.colore || muiTheme.palette.primary.main}
              fill={calendari.find((c) => String(c.id) === String(eventCalendarId))?.colore || muiTheme.palette.primary.main}
            />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {eventMode === 'edit' ? 'Gestione evento' : 'Nuovo evento'}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              if (eventSaving) return;
              setEventOpen(false);
              resetEventForm();
            }}
          >
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <TextField
            fullWidth
            label="Titolo"
            value={eventTitolo}
            onChange={(e) => setEventTitolo(e.target.value)}
            variant="outlined"
          />

          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="evento-calendario-label">Calendario</InputLabel>
              <Select
                labelId="evento-calendario-label"
                label="Calendario"
                value={eventCalendarId}
                onChange={(e) => setEventCalendarId(e.target.value)}
              >
                {calendari
                  .slice()
                  .sort((a, b) => a.ordine - b.ordine || a.nome.localeCompare(b.nome, 'it'))
                  .map((c) => {
                    const isPrivato = String(c.visibilita || '').toLowerCase() === 'privata';
                    return (
                      <MenuItem key={c.id} value={String(c.id)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Square size={14} color={c.colore} fill={c.colore} />
                          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                            {c.nome}
                          </Typography>
                          {isPrivato ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Lock size={14} color={c.colore} />
                            </Box>
                          ) : null}
                        </Box>
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          </Box>

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 700, color: 'text.secondary' }}>Tutto il giorno</Typography>
            <Switch
              checked={eventAllDay}
              onChange={(e) => {
                const next = e.target.checked;
                setEventAllDay(next);
                if (next) {
                  const base = fromDateTimeLocalValue(eventStart) || new Date();
                  const date = toDateOnlyValue(base);
                  setEventStartDate(date);
                  setEventEndDate(date);
                  setEventStart('');
                  setEventEnd('');
                } else {
                  const base = fromDateOnlyValue(eventStartDate) || new Date();
                  base.setHours(9, 0, 0, 0);
                  const end = addMinutes(base, 60);
                  setEventStart(toDateTimeLocalValue(base));
                  setEventEnd(toDateTimeLocalValue(end));
                  setEventStartDate('');
                  setEventEndDate('');
                }
              }}
            />
          </Stack>

          {eventAllDay ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 1.5 }}>
              <TextField
                fullWidth
                type="date"
                label="Data inizio"
                value={eventStartDate}
                onChange={(e) => setEventStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                type="date"
                label="Data fine"
                value={eventEndDate}
                onChange={(e) => setEventEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 1.5 }}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Inizio"
                value={eventStart}
                onChange={(e) => setEventStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                type="datetime-local"
                label="Fine"
                value={eventEnd}
                onChange={(e) => setEventEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}

          <TextField
            fullWidth
            label="Timezone"
            value="Europe/Rome"
            disabled
            variant="outlined"
            sx={{ mt: 1.5 }}
          />

          <TextField
            fullWidth
            label="Luogo"
            value={eventLuogo}
            onChange={(e) => setEventLuogo(e.target.value)}
            variant="outlined"
            sx={{ mt: 2 }}
          />

          <TextField
            fullWidth
            label="Note"
            value={eventNote}
            onChange={(e) => setEventNote(e.target.value)}
            multiline
            minRows={3}
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          {eventMode === 'edit' ? (
            <Button color="error" variant="outlined" onClick={deleteEvent} disabled={eventSaving}>
              Elimina
            </Button>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
          <Button
            variant="outlined"
            onClick={() => {
              if (eventSaving) return;
              setEventOpen(false);
              resetEventForm();
            }}
          >
            Annulla
          </Button>
          <Button variant="contained" onClick={saveEvent} disabled={eventSaving}>
            {eventSaving ? 'Salvataggio...' : 'Salva evento'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createOpen}
        onClose={() => {
          if (createSaving) return;
          setCreateOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <CalendarPlus size={20} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Nuovo calendario
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              if (createSaving) return;
              setCreateOpen(false);
            }}
          >
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Nome"
              value={createNome}
              onChange={(e) => setCreateNome(e.target.value)}
              placeholder="Es. Mediazioni, Consulenze CTU..."
              variant="outlined"
            />

            <TextField
              fullWidth
              label="Descrizione"
              multiline
              minRows={3}
              value={createDescrizione}
              onChange={(e) => setCreateDescrizione(e.target.value)}
              placeholder="Breve descrizione del calendario..."
              variant="outlined"
            />

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Colore
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {COLORI_PRESET.map((color) => {
                  const selected = color.toLowerCase() === String(createColore).toLowerCase();
                  return (
                    <Box
                      key={color}
                      role="button"
                      tabIndex={0}
                      onClick={() => setCreateColore(color)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') setCreateColore(color);
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: color,
                        cursor: 'pointer',
                        border: selected ? '2px solid' : '2px solid transparent',
                        borderColor: selected ? 'primary.main' : 'transparent'
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <FormControl fullWidth>
              <InputLabel id="agenda-tipo-label">Tipo</InputLabel>
              <Select
                labelId="agenda-tipo-label"
                label="Tipo"
                value={isAdmin ? createTipo : 'personale'}
                onChange={(e) => setCreateTipo(e.target.value)}
                disabled={!isAdmin}
              >
                <MenuItem value="sistema">Sistema (studio)</MenuItem>
                <MenuItem value="personale">Personale</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="agenda-icona-label">Icona</InputLabel>
              <Select
                labelId="agenda-icona-label"
                label="Icona"
                value={createIcona}
                onChange={(e) => setCreateIcona(e.target.value)}
              >
                <MenuItem value="calendario">📅 Calendario</MenuItem>
                <MenuItem value="tribunale">🏛️ Tribunale</MenuItem>
                <MenuItem value="scadenza">⏰ Scadenza</MenuItem>
                <MenuItem value="riunione">👥 Riunione</MenuItem>
                <MenuItem value="lavoro">💼 Lavoro</MenuItem>
                <MenuItem value="documento">📄 Documento</MenuItem>
              </Select>
            </FormControl>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Visibilità
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                <Button
                  size="small"
                  variant={createVisibilita === 'pubblica' ? 'contained' : 'outlined'}
                  onClick={() => setCreateVisibilita('pubblica')}
                  disabled={!isAdmin}
                  sx={{ justifyContent: 'center' }}
                  startIcon={<Globe size={16} />}
                >
                  Pubblica
                </Button>
                <Button
                  size="small"
                  variant={createVisibilita === 'privata' ? 'contained' : 'outlined'}
                  onClick={() => setCreateVisibilita('privata')}
                  sx={{ justifyContent: 'center' }}
                  startIcon={<Lock size={16} />}
                >
                  Privata
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              if (createSaving) return;
              setCreateOpen(false);
              resetCreateForm();
            }}
            variant="outlined"
          >
            Annulla
          </Button>
          <Button onClick={handleCreateCalendar} variant="contained" disabled={createSaving}>
            {createSaving ? 'Creazione...' : 'Crea calendario'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={() => {
          if (deleteSaving) return;
          setDeleteOpen(false);
          setDeleteTarget(null);
          setDeleteEventCount(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Elimina calendario
          </Typography>
          <IconButton
            onClick={() => {
              if (deleteSaving) return;
              setDeleteOpen(false);
              setDeleteTarget(null);
              setDeleteEventCount(null);
            }}
          >
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">Vuoi eliminare il calendario "{deleteTarget?.nome}"?</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {deleteEventCountLoading
              ? 'Calcolo eventi collegati...'
              : deleteEventCount != null
                ? `Questa operazione eliminerà anche ${deleteEventCount} eventi collegati ed è irreversibile.`
                : 'Questa operazione eliminerà anche gli eventi collegati ed è irreversibile.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              if (deleteSaving) return;
              setDeleteOpen(false);
              setDeleteTarget(null);
              setDeleteEventCount(null);
            }}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteCalendar}
            disabled={deleteSaving || deleteEventCountLoading}
          >
            {deleteSaving ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
